import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type Selection = {
  document_id: string;
  pages: string;
  blob_id?: string;
  reason?: string;
};
type PacketType = {
  key: string;
  label: string;
  selections: Selection[];
  missing: string[];
  subject: string;
  body: string;
  introduction?: string;
  reassurance?: string | null;
};
type Document = {
  id: string;
  name: string;
  label: string;
  summary?: string | null;
  emailUrl?: string | null;
  kind: string;
  date: string;
  source: string;
  size: number | null;
  supported: boolean;
  sourceState: string;
  url: string;
  blobId?: string;
  booking?: { operationId: string | null; introduction: string; signed?: boolean; scheduleChanged?: boolean } | null;
};
type Packet = {
  id: string | null;
  packetType: string;
  status: string;
  version: number;
  mailboxId: string;
  to: string;
  cc: string;
  subject: string;
  body: string;
  selections: Selection[];
  url: string | null;
  sendUrl: string | null;
  gmailDraftUrl?: string | null;
  gmailDraftSavedAt?: string | null;
  gmailDraftMailbox?: string | null;
  gmailDraftSyncStatus?: "idle" | "saving" | "unknown";
  sentAt?: string;
  attachments: { name: string; url: string }[];
};
type Props = {
  patientName: string;
  patientUrl: string;
  csrfToken: string;
  packet: Packet;
  types: PacketType[];
  documents: Document[];
  mailboxes: { id: string; address: string; signature: string }[];
  defaultMailboxId?: string;
  recentPackets: { id: string; label: string; url: string }[];
  bookingOperations?: { id: string; label: string; bookingSheetAvailable: boolean; upcoming: boolean; editUrl?: string }[];
  urls: {
    create: string;
    upload: string;
    info: string;
    booking: string;
    generateBooking?: string;
    order: string;
    new: string;
  };
};
type Preview = { document: Document; pageCount: number; previewUrl: string };
const filters = ["All", "Suggested", "Notes", "Labs", "Insurance"];
const filesize = (bytes: number | null) =>
  !bytes
    ? "Size checked before sending"
    : bytes < 1024 * 1024
      ? `${Math.ceil(bytes / 1024)} KB`
      : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
const selectedPages = (text: string, count: number): number[] => {
  if (!text.trim())
    return Array.from({ length: count }, (_, index) => index + 1);
  return text.split(",").flatMap((part) => {
    const [start, end = start] = part.trim().split("-").map(Number);
    return start >= 1 && end >= start && end <= count
      ? Array.from({ length: end - start + 1 }, (_, index) => start + index)
      : [];
  });
};

function openDocumentWindow(url: string) {
  window.open(
    url,
    "_blank",
    "popup=yes,width=1280,height=900,resizable=yes,scrollbars=yes,noopener,noreferrer",
  );
}

export default function PatientPacketWorkspace(props: Props) {
  const initialType =
    props.types.find((type) => type.key === props.packet.packetType) ||
    props.types[0];
  const firstMailbox = props.defaultMailboxId || "";
  const withSignature = (body: string, mailboxId: string) => {
    const signature = props.mailboxes.find(
      (mailbox) => mailbox.id === mailboxId,
    )?.signature;
    return signature ? `${body}\n\n${signature}` : body;
  };
  const suggestions = (type: PacketType) =>
    type.selections.map((item) => ({
      ...item,
      blob_id: props.documents.find(
        (document) => document.id === item.document_id,
      )?.blobId,
    }));
  const [packet, setPacket] = useState<Packet>(() =>
    props.packet.id
      ? props.packet
      : {
          ...props.packet,
          mailboxId: firstMailbox,
          subject: initialType.subject,
          body: withSignature(initialType.body, firstMailbox),
          selections: suggestions(initialType),
        },
  );
  const [documents, setDocuments] = useState(props.documents);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewHost, setPreviewHost] = useState<HTMLElement | null>(null);
  const [previewError, setPreviewError] = useState("");
  const previewWindow = useRef<Window | null>(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [dirty, setDirty] = useState(!props.packet.id);
  const [ccVisible, setCcVisible] = useState(Boolean(props.packet.cc));
  const typeDrafts = useRef(
    new Map<
      string,
      Pick<Packet, "to" | "cc" | "subject" | "body" | "selections">
    >(),
  );
  const fileInput = useRef<HTMLInputElement>(null);
  const bodyInput = useRef<HTMLTextAreaElement>(null);
  const type =
    props.types.find((item) => item.key === packet.packetType) ||
    props.types[0];
  const suggestedIds = new Set(type.selections.map((item) => item.document_id));
  const editable = packet.status === "draft" && packet.gmailDraftSyncStatus !== "saving";
  const gmailBlocked = packet.gmailDraftSyncStatus === "saving" || packet.gmailDraftSyncStatus === "unknown";
  const documentById = new Map(
    documents.map((document) => [document.id, document]),
  );
  const selectedBookings = packet.selections.flatMap((selection) => {
    const document = documentById.get(selection.document_id);
    return document?.kind === "booking_sheet_pdf" ? [document] : [];
  });
  const missing = type.missing.filter((label) => label !== "Signed booking sheet" || !selectedBookings.some((document) => document.booking?.signed));
  const [bookingOperationId, setBookingOperationId] = useState(() => {
    const selections = props.packet.id ? props.packet.selections : initialType.selections;
    const booking = props.documents.find((document) => document.kind === "booking_sheet_pdf" && selections.some((item) => item.document_id === document.id));
    const upcoming = props.bookingOperations?.filter((operation) => operation.upcoming);
    return booking?.booking?.operationId || (upcoming?.length === 1 ? upcoming[0].id : "");
  });
  const bookingOperation = props.bookingOperations?.find((operation) => operation.id === bookingOperationId);
  const bookingEditorUrl = `${props.urls.booking}?patient_operation_id=${encodeURIComponent(bookingOperationId)}`;
  const available = documents.filter((document) => {
    const matchesQuery =
      `${document.name} ${document.label} ${document.summary || ""} ${document.source} ${document.date}`
        .toLowerCase()
        .includes(query.toLowerCase());
    const matchesFilter =
      filter === "All" ||
      (filter === "Suggested" && suggestedIds.has(document.id)) ||
      (filter === "Insurance" && document.kind === "insurance_card") ||
      (filter === "Labs" && document.kind === "lab") ||
      (filter === "Notes" && /note|medical_record/.test(document.kind));
    return matchesQuery && matchesFilter;
  });

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (dirty && editable) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, editable]);
  useEffect(() => {
    if (bodyInput.current) {
      bodyInput.current.style.height = "auto";
      bodyInput.current.style.height = `${Math.max(bodyInput.current.scrollHeight, 200)}px`;
    }
  }, [packet.body]);

  useEffect(() => {
    const closeWindow = () => {
      const popup = previewWindow.current;
      previewWindow.current = null;
      popup?.close();
    };
    window.addEventListener("pagehide", closeWindow);
    return () => {
      window.removeEventListener("pagehide", closeWindow);
      closeWindow();
    };
  }, []);

  function closePageSelector() {
    const popup = previewWindow.current;
    previewWindow.current = null;
    setPreviewHost(null);
    setPreview(null);
    setPreviewError("");
    popup?.close();
  }

  function change(values: Partial<Packet>) {
    if (values.selections) {
      const bookings = values.selections.flatMap((item) => {
        const document = documentById.get(item.document_id);
        return document?.booking ? [document.booking] : [];
      });
      if (bookings.length === 1 && bookings[0].operationId) setBookingOperationId(bookings[0].operationId);
    }
    setPacket((current) => {
      const next = { ...current, ...values };
      if (values.selections && next.packetType === "surgery_booking") {
        next.body = bookingBody(next.body, values.selections, documents);
      }
      return next;
    });
    setDirty(true);
    setNotice("");
    setError("");
  }
  function bookingBody(body: string, selections: Selection[], library: Document[], insert = false) {
    const bookingType = props.types.find((item) => item.key === "surgery_booking");
    if (!bookingType?.introduction) return body;
    const selected = library.filter((document) => document.kind === "booking_sheet_pdf" && selections.some((item) => item.document_id === document.id));
    const introduction = selected.length === 1 ? selected[0].booking?.introduction || bookingType.introduction : bookingType.introduction;
    const known = new Set([bookingType.introduction, ...library.flatMap((document) => document.booking ? [document.booking.introduction] : [])]);
    const paragraphs = body.split("\n\n");
    const prefix = `${bookingType.introduction.replace(/\.$/, "")} - Booking for `;
    const index = paragraphs.findIndex((paragraph) => known.has(paragraph) || (paragraph.startsWith(prefix) && /^\d{2}\/\d{2}\/\d{4} @ (\d{1,2}:\d{2} [AP]M|time to be confirmed)\.$/.test(paragraph.slice(prefix.length))));
    if (index >= 0) paragraphs[index] = introduction;
    else if (insert) paragraphs.splice(paragraphs[0] === "Hello," ? 1 : 0, 0, introduction);
    return paragraphs.join("\n\n");
  }
  function toggleReassurance(checked: boolean) {
    const reassurance = type.reassurance;
    if (!reassurance) return;
    if (!checked) {
      change({ body: packet.body.split(reassurance).join("").replace(/\n{3,}/g, "\n\n").trim() });
      return;
    }
    const closing = "Please let us know if you have any questions.";
    if (packet.body.includes(closing)) {
      change({ body: packet.body.replace(closing, `${reassurance}\n\n${closing}`) });
    } else {
      const signature = props.mailboxes.find((mailbox) => mailbox.id === packet.mailboxId)?.signature;
      const body = signature && packet.body.endsWith(`\n\n${signature}`) ? packet.body.slice(0, -(signature.length + 2)) : packet.body;
      change({ body: `${body}\n\n${reassurance}${body !== packet.body ? `\n\n${signature}` : ""}` });
    }
  }
  function changeBookingOperation(id: string) {
    const operation = props.bookingOperations?.find((item) => item.id === id);
    change({ selections: packet.selections.filter((item) => {
      const document = documentById.get(item.document_id);
      return document?.kind !== "booking_sheet_pdf" || (operation?.bookingSheetAvailable && document.booking?.operationId === id);
    }) });
    setBookingOperationId(id);
  }
  async function generateBookingSheet() {
    if (!props.urls.generateBooking || !bookingOperation?.bookingSheetAvailable) return;
    setBusy("Generating signed booking sheet…");
    setError("");
    setNotice("");
    try {
      const document: Document = await request(props.urls.generateBooking, "POST", { patient_operation_id: bookingOperationId });
      const library = [document, ...documents];
      const remaining = packet.selections.filter((item) => documentById.get(item.document_id)?.kind !== "booking_sheet_pdf");
      const selections = [{ document_id: document.id, pages: "", blob_id: document.blobId }, ...remaining];
      setDocuments(library);
      // Use the new library now; state updates have not rendered the new file yet.
      setPacket((current) => ({ ...current, selections, body: bookingBody(current.body, selections, library) }));
      setDirty(true);
      setNotice("Signed booking sheet generated and attached. Review the PDF and email before sending.");
    } catch (exception) {
      setError((exception as Error).message);
    } finally {
      setBusy("");
    }
  }
  function changeType(key: string) {
    typeDrafts.current.set(packet.packetType, {
      to: packet.to,
      cc: packet.cc,
      subject: packet.subject,
      body: packet.body,
      selections: packet.selections,
    });
    const next = props.types.find((item) => item.key === key)!;
    change({
      packetType: key,
      ...(typeDrafts.current.get(key) || {
        to: "",
        cc: "",
        subject: next.subject,
        body: withSignature(next.body, packet.mailboxId),
        selections: suggestions(next),
      }),
    });
    setFilter("All");
    setQuery("");
    closePageSelector();
  }
  function toggle(document: Document) {
    change({
      selections: packet.selections.some(
        (item) => item.document_id === document.id,
      )
        ? packet.selections.filter((item) => item.document_id !== document.id)
        : [
            ...packet.selections,
            { document_id: document.id, pages: "", blob_id: document.blobId },
          ],
    });
  }
  function pages(id: string, value: string) {
    change({
      selections: packet.selections.map((item) =>
        item.document_id === id ? { ...item, pages: value } : item,
      ),
    });
  }
  function move(index: number, delta: number) {
    const selections = [...packet.selections];
    [selections[index], selections[index + delta]] = [
      selections[index + delta],
      selections[index],
    ];
    change({ selections });
  }
  async function request(url: string, method = "GET", data?: unknown) {
    const isForm = data instanceof FormData;
    const response = await fetch(url, {
      method,
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": props.csrfToken,
        ...(!isForm && data ? { "Content-Type": "application/json" } : {}),
      },
      body: data
        ? ((isForm ? data : JSON.stringify(data)) as BodyInit)
        : undefined,
    });
    if (!response.headers.get("content-type")?.includes("application/json"))
      throw new Error(
        "Your session may have expired. Save your text and refresh this page.",
      );
    const result = await response.json();
    if (!response.ok)
      throw new Error(result.error || "Unable to complete this request.");
    return result;
  }
  async function save(): Promise<Packet> {
    const result: Packet = await request(
      packet.url || props.urls.create,
      packet.id ? "PATCH" : "POST",
      {
        packet: {
          packet_type: packet.packetType,
          gmail_mailbox_id: packet.mailboxId || null,
          recipient_email: packet.to,
          cc_email: packet.cc,
          email_subject: packet.subject,
          email_body: packet.body,
          selections: packet.selections,
          lock_version: packet.version,
        },
      },
    );
    setPacket(result);
    setDirty(false);
    if (result.url) window.history.replaceState(null, "", result.url);
    return result;
  }
  async function saveDraft() {
    setBusy("Saving draft…");
    setError("");
    setNotice("");
    try {
      await save();
      setNotice(
        "Draft saved. You can return to this packet from the patient’s Prepare packet page.",
      );
    } catch (exception) {
      setError((exception as Error).message);
    } finally {
      setBusy("");
    }
  }
  async function saveGmailDraft() {
    setBusy("Saving draft in Gmail…");
    setError("");
    setNotice("");
    let saved: Packet | undefined;
    try {
      saved = await save();
      const result: Packet = await request(saved.gmailDraftUrl!, "POST", {
        lock_version: saved.version,
      });
      setPacket(result);
      setNotice(`Draft saved in Gmail for ${result.gmailDraftMailbox}. A copy is also saved in the app.`);
    } catch (exception) {
      setError((saved ? "Your app draft is saved. " : "") + (exception as Error).message);
      if (saved?.url) {
        try {
          setPacket(await request(saved.url));
        } catch {
          setPacket({ ...saved, gmailDraftSyncStatus: "unknown" });
        }
      }
    } finally {
      setBusy("");
    }
  }
  async function send() {
    setBusy("Preparing and sending…");
    setError("");
    setNotice("");
    let saved: Packet | undefined;
    try {
      saved = await save();
      const result = await request(saved.sendUrl!, "POST", {
        lock_version: saved.version,
      });
      setPacket(result);
      setDirty(false);
      setNotice(
        "Packet sent. A copy of the exact attachments is saved below and the delivery is recorded in the patient timeline.",
      );
    } catch (exception) {
      setError((exception as Error).message);
      if (saved?.url) {
        try {
          setPacket(await request(saved.url));
        } catch {
          setPacket({ ...saved, status: "unknown" });
        }
      }
    } finally {
      setBusy("");
    }
  }
  async function upload(file?: File) {
    if (!file) return;
    setBusy("Adding document…");
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const document: Document = await request(props.urls.upload, "POST", form);
      setDocuments((current) => [document, ...current]);
      change({
        selections: [
          ...packet.selections,
          { document_id: document.id, pages: "", blob_id: document.blobId },
        ],
      });
      setNotice("Document added to this patient and selected for the packet.");
    } catch (exception) {
      setError((exception as Error).message);
    } finally {
      setBusy("");
      if (fileInput.current) fileInput.current.value = "";
    }
  }
  async function openPageSelector(document: Document) {
    closePageSelector();
    const popup = window.open(
      "",
      "_blank",
      "popup=yes,width=1280,height=900,resizable=yes,scrollbars=yes",
    );
    if (!popup) {
      setError("Allow pop-up windows for this site, then select pages again.");
      return;
    }
    previewWindow.current = popup;
    popup.opener = null;
    popup.document.title = `Select pages — ${document.name}`;
    popup.document.documentElement.className = window.document.documentElement.className;
    window.document.querySelectorAll('link[rel="stylesheet"], style').forEach((style) => {
      popup.document.head.appendChild(style.cloneNode(true));
    });
    const host = popup.document.createElement("div");
    popup.document.body.appendChild(host);
    setPreviewHost(host);
    popup.addEventListener("beforeunload", () => {
      if (previewWindow.current === popup) {
        previewWindow.current = null;
        setPreviewHost(null);
        setPreview(null);
      }
    });
    popup.focus();
    setBusy("Loading document…");
    setError("");
    try {
      const result = await request(
        `${props.urls.info}?document_id=${encodeURIComponent(document.id)}`,
      );
      if (previewWindow.current !== popup || popup.closed) return;
      setPreview({ document, ...result });
      setDocuments((current) =>
        current.map((item) =>
          item.id === document.id ? { ...item, blobId: result.blobId } : item,
        ),
      );
      // The preview has just loaded these exact bytes for review.
      if (editable)
        change({
          selections: packet.selections.map((item) =>
            item.document_id === document.id
              ? { ...item, blob_id: result.blobId }
              : item,
          ),
        });
    } catch (exception) {
      closePageSelector();
      setError((exception as Error).message);
    } finally {
      setBusy("");
    }
  }
  function changeMailbox(id: string) {
    const previous = props.mailboxes.find(
      (mailbox) => mailbox.id === packet.mailboxId,
    )?.signature;
    const body =
      previous && packet.body.endsWith(`\n\n${previous}`)
        ? packet.body.slice(0, -(previous.length + 2))
        : packet.body;
    change({ mailboxId: id, body: withSignature(body, id) });
  }
  const previewSelection =
    preview &&
    packet.selections.find((item) => item.document_id === preview.document.id);
  const checkedPages = preview
    ? selectedPages(previewSelection?.pages || "", preview.pageCount)
    : [];

  return (
    <div className="patient-packet">
      {error && (
        <div role="alert" className="pp-alert pp-error">
          {error}
        </div>
      )}
      {notice && (
        <div role="status" className="pp-alert pp-success">
          {notice}
        </div>
      )}
      {packet.status === "draft" && packet.gmailDraftSavedAt && (
        <div className="pp-alert">
          Last saved in Gmail for {packet.gmailDraftMailbox} on {new Date(packet.gmailDraftSavedAt).toLocaleString()}.
          {" "}App edits stay here until you choose Save draft in Gmail again.
        </div>
      )}
      {gmailBlocked && (
        <div className="pp-alert pp-error" role="alert">
          {packet.gmailDraftSyncStatus === "saving"
            ? "A Gmail save is in progress. Refresh to check its result."
            : "The last Gmail save could not be confirmed. Check the From mailbox and contact support before saving to Gmail or sending again. You can still save edits in the app."}
        </div>
      )}
      {packet.status !== "draft" && (
        <div className="pp-alert">
          {packet.status === "sent"
            ? `Sent${packet.sentAt ? ` on ${new Date(packet.sentAt).toLocaleString()}` : ""}. This is the saved delivery record.`
            : "Delivery may have reached Gmail. Check the sender’s Sent mailbox before preparing another packet. This packet is locked to prevent duplicate delivery."}{" "}
          <a href={props.urls.new}>Prepare another packet</a>
        </div>
      )}
      <fieldset disabled={Boolean(busy) || !editable} className="pp-fields">
        <section className="pp-card pp-type">
          <div>
            <label htmlFor="packet-type">Packet type</label>
            <select
              id="packet-type"
              value={packet.packetType}
              onChange={(event) => changeType(event.target.value)}
            >
              {props.types.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <div className="pp-type-detail">
            <strong>
              {type.key === "custom"
                ? "Build a packet from any patient documents"
                : "A starting selection, ready for your review"}
            </strong>
            <p>
              {type.key === "custom"
                ? "Add files from the list below or upload a document."
                : "Suggestions use document type and recency. Confirm the correct study, operation, dates and pages before sending."}
            </p>
          </div>
        </section>
        {missing.length > 0 && (
          <div className="pp-missing">
            <strong>No automatic selection:</strong> {missing.join(" · ")}.
            Missing or ambiguous matches are left for you to choose.{" "}
            {packet.packetType === "imaging_referral" && (
              <a href={props.urls.order} target="_blank" rel="noreferrer">
                Prepare order slip ↗
              </a>
            )}
          </div>
        )}
        {packet.packetType === "surgery_booking" && (
          <section className="pp-card pp-booking" aria-label="Booking sheet">
            <h2>Banner Desert booking sheet</h2>
            <p>Generate a signed sheet using the operation’s current date and time. You can edit the sheet first; save changes in the editor before generating here.</p>
            <label htmlFor="packet-booking-operation">Generate sheet for operation</label>
            <select id="packet-booking-operation" value={bookingOperationId} onChange={(event) => changeBookingOperation(event.target.value)}>
              <option value="">Select an operation</option>
              {props.bookingOperations?.map((operation) => <option key={operation.id} value={operation.id}>{operation.label}</option>)}
            </select>
            {bookingOperation && !bookingOperation.bookingSheetAvailable && <p>Booking sheets are only used for Banner Desert, not the surgery center. You can still prepare a packet with other documents.</p>}
            {bookingOperation?.bookingSheetAvailable && <div className="pp-booking-actions">
              <button type="button" className="pp-button" onClick={generateBookingSheet}>Generate & attach signed sheet</button>
              <a href={bookingEditorUrl} target="_blank" rel="noopener noreferrer">Review / edit booking sheet ↗</a>
              {bookingOperation.editUrl && <a href={bookingOperation.editUrl} target="_blank" rel="noopener noreferrer">Edit operation date / time ↗</a>}
            </div>}
            {bookingOperation?.bookingSheetAvailable && <p>Generating replaces any booking sheet selected below. Other attachments and your email edits are kept.</p>}
            {selectedBookings.length === 1 && selectedBookings[0].booking?.operationId && <div className="pp-booking-details">
              <p>{selectedBookings[0].booking.introduction}</p>
              {selectedBookings[0].booking.scheduleChanged && <p>The selected booking sheet’s date/time differs from its operation. Generate a new sheet to match the current schedule.</p>}
              <button type="button" className="pp-link" onClick={() => change({ body: bookingBody(packet.body, packet.selections, documents, true) })}>Use booking details in email</button>
            </div>}
            {(selectedBookings.length > 1 || (selectedBookings.length === 1 && !selectedBookings[0].booking?.operationId)) && <p>Choose one booking sheet linked to an operation to include its surgery date and time in the email.</p>}
          </section>
        )}
        <div className="pp-columns">
          <section className="pp-card pp-library">
            <div className="pp-section-heading">
              <div>
                <h2>
                  Patient documents <span>{documents.length}</span>
                </h2>
                <p>Choose files to include in this packet.</p>
              </div>
              <button
                type="button"
                className="pp-button"
                onClick={() => fileInput.current?.click()}
              >
                ＋ Add document
              </button>
              <input
                ref={fileInput}
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                onChange={(event) => upload(event.target.files?.[0])}
                className="pp-file-input"
                aria-label="Upload a document"
              />
            </div>
            <input
              type="search"
              aria-label="Quick filter documents"
              placeholder="Quick filter by name, description, type or date…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <div className="pp-filters" aria-label="Document categories">
              {filters.map((item) => (
                <button
                  type="button"
                  key={item}
                  aria-pressed={filter === item}
                  onClick={() => setFilter(item)}
                >
                  {item}
                </button>
              ))}
            </div>
            <div
              className="pp-document-list"
              role="region"
              aria-label="Patient documents"
              tabIndex={0}
            >
              {available.map((document) => {
                const selected = packet.selections.some(
                  (item) => item.document_id === document.id,
                );
                return (
                  <div
                    className={`pp-document ${selected ? "pp-selected" : ""}`}
                    key={document.id}
                  >
                    <input
                      type="checkbox"
                      aria-label={`Include ${document.name}`}
                      checked={selected}
                      disabled={!document.supported}
                      onChange={() => toggle(document)}
                    />
                    <div className="pp-document-info">
                      <div className="pp-document-line">
                        <strong
                          title={`${document.name} · ${document.label} · ${document.source} · ${document.sourceState} · ${filesize(document.size)}`}
                        >
                          {document.name}
                        </strong>
                        <time dateTime={document.date}>{document.date}</time>
                        {suggestedIds.has(document.id) && (
                          <span
                            className="pp-suggested"
                            title="Suggested document"
                            aria-label="Suggested document"
                          >
                            ★
                          </span>
                        )}
                        {!document.supported && (
                          <span title="PDF, PNG and JPEG supported">Unsupported</span>
                        )}
                      </div>
                      {document.summary && (
                        <small
                          className="pp-document-summary"
                          title={document.summary}
                        >
                          {document.summary}
                        </small>
                      )}
                    </div>
                    <div className="pp-document-actions">
                      <button
                        type="button"
                        className="pp-link"
                        disabled={!document.supported}
                        aria-label={`Preview ${document.name}`}
                        onClick={() => openDocumentWindow(document.url)}
                      >
                        Preview
                      </button>
                      {document.emailUrl && (
                        <a
                          href={document.emailUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open source email thread in a new window"
                          className="pp-link"
                          aria-label={`Email thread for ${document.name}`}
                          onClick={(event) => {
                            event.preventDefault();
                            openDocumentWindow(document.emailUrl!);
                          }}
                        >
                          Email thread
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
              {!available.length && (
                <div className="pp-empty">
                  {documents.length
                    ? "No matching documents. Try another filter."
                    : "No documents are linked to this patient yet. Add a document to get started."}
                </div>
              )}
            </div>
          </section>
          <section className="pp-card pp-selection">
            <div className="pp-section-heading">
              <div>
                <h2>
                  Selected documents <span>{packet.selections.length}</span>
                </h2>
                <p>Attached as PDFs, in this order.</p>
              </div>
            </div>
            {packet.selections.length ? (
              <ol>
                {packet.selections.map((selection, index) => {
                  const document = documentById.get(selection.document_id);
                  return (
                    <li key={selection.document_id}>
                      <span className="pp-number">{index + 1}</span>
                      <div className="pp-selection-info">
                        <strong
                          title={[document?.name, selection.reason]
                            .filter(Boolean)
                            .join(" · ")}
                        >
                          {document?.name || "Document no longer available"}
                        </strong>
                        <div className="pp-selection-controls">
                          <label className="pp-pages-label">
                            Pages
                            <input
                              aria-label={`Pages for ${document?.name || selection.document_id}`}
                              title="Leave blank for all pages, or enter a range such as 2-5, 8"
                              placeholder="All pages"
                              value={selection.pages}
                              onChange={(event) =>
                                pages(selection.document_id, event.target.value)
                              }
                            />
                          </label>
                          {document && (
                            <button
                              type="button"
                              className="pp-link"
                              onClick={() => openPageSelector(document)}
                            >
                              Select pages
                            </button>
                          )}
                          {document?.emailUrl && (
                            <a
                              href={document.emailUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open source email thread in a new window"
                              className="pp-link"
                              aria-label={`Source email thread for ${document.name}`}
                              onClick={(event) => {
                                event.preventDefault();
                                openDocumentWindow(document.emailUrl!);
                              }}
                            >
                              Email thread
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="pp-order-buttons">
                        <button
                          type="button"
                          aria-label={`Move ${document?.name} up`}
                          disabled={index === 0}
                          onClick={() => move(index, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          aria-label={`Move ${document?.name} down`}
                          disabled={index === packet.selections.length - 1}
                          onClick={() => move(index, 1)}
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          aria-label={`Remove ${document?.name}`}
                          onClick={() =>
                            change({
                              selections: packet.selections.filter(
                                (item) =>
                                  item.document_id !== selection.document_id,
                              ),
                            })
                          }
                        >
                          ×
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <div className="pp-empty">
                Select documents from the list to build your packet.
              </div>
            )}
            <div className="pp-selection-footer">
              Up to 25 documents · 18 MB total
              <br />
              Review fax pages to exclude cover sheets or unrelated records.
            </div>
          </section>
        </div>
        {previewHost && createPortal(
          <div className="patient-packet pp-page-window">
            <fieldset disabled={Boolean(busy) || !editable} className="pp-fields">
              {preview ? <section className="pp-card pp-preview">
                <div className="pp-section-heading">
                  <div>
                    <h2>{preview.document.name}</h2>
                    <p>
                      {preview.pageCount} pages ·{" "}
                      {previewSelection
                        ? "Changes update your packet draft immediately."
                        : "Add this document to select its pages."}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="pp-button"
                    onClick={closePageSelector}
                  >
                    Done
                  </button>
                </div>
                {previewError && <div className="pp-alert pp-error" role="alert">{previewError}</div>}
                <div className="pp-preview-body">
                  <iframe
                    title={`Document preview: ${preview.document.name}`}
                    src={preview.previewUrl}
                  />
                  <div className="pp-page-choices">
                    {previewSelection ? (
                      <>
                        <div className="pp-page-actions">
                          <button
                            type="button"
                            className="pp-link"
                            onClick={() => {
                              setPreviewError("");
                              pages(preview.document.id, "");
                            }}
                          >
                            All pages
                          </button>
                          <button
                            type="button"
                            className="pp-link"
                            onClick={() =>
                              openDocumentWindow(
                                `${preview.previewUrl}&pages=${encodeURIComponent(previewSelection.pages)}`,
                              )
                            }
                          >
                            Preview selected ↗
                          </button>
                        </div>
                        {Array.from(
                          { length: preview.pageCount },
                          (_, index) => index + 1,
                        ).map((page) => (
                          <label key={page}>
                            <input
                              type="checkbox"
                              checked={checkedPages.includes(page)}
                              onChange={() => {
                                const next = checkedPages.includes(page)
                                  ? checkedPages.filter((number) => number !== page)
                                  : [...checkedPages, page].sort((a, b) => a - b);
                                if (!next.length) {
                                  setPreviewError(
                                    "Keep at least one page selected, or remove the document from the packet.",
                                  );
                                  return;
                                }
                                setPreviewError("");
                                pages(
                                  preview.document.id,
                                  next.length === preview.pageCount
                                    ? ""
                                    : next.join(", "),
                                );
                              }}
                            />
                            Page {page}
                          </label>
                        ))}
                      </>
                    ) : (
                      <button
                        type="button"
                        className="pp-button"
                        onClick={() => toggle(preview.document)}
                      >
                        Add to packet
                      </button>
                    )}
                  </div>
                </div>
              </section> : <div className="pp-card pp-empty" role="status">Loading document…</div>}
            </fieldset>
          </div>,
          previewHost,
        )}
        <section className="pp-card pp-email">
          <div className="pp-section-heading">
            <div>
              <h2>Email preview</h2>
              <p>Edit the recipients and message before sending.</p>
            </div>
            <span className="pp-status">
              {packet.selections.length} PDF
              {packet.selections.length === 1 ? "" : "s"} attached
            </span>
          </div>
          <div className="pp-email-fields">
            <label htmlFor="packet-from">From</label>
            <select
              id="packet-from"
              value={packet.mailboxId}
              onChange={(event) => changeMailbox(event.target.value)}
            >
              <option value="">Select sender mailbox</option>
              {props.mailboxes.map((mailbox) => (
                <option value={mailbox.id} key={mailbox.id}>
                  {mailbox.address}
                </option>
              ))}
            </select>
            <label htmlFor="packet-to">To</label>
            <div className="pp-recipient">
              <input
                id="packet-to"
                type="text"
                inputMode="email"
                autoComplete="off"
                placeholder="Recipient email, separated by commas"
                value={packet.to}
                onChange={(event) => change({ to: event.target.value })}
              />
              <button
                type="button"
                className="pp-link"
                onClick={() => setCcVisible(!ccVisible)}
              >
                Cc
              </button>
            </div>
            {(ccVisible || packet.cc) && (
              <>
                <label htmlFor="packet-cc">Cc</label>
                <input
                  id="packet-cc"
                  inputMode="email"
                  autoComplete="off"
                  value={packet.cc}
                  onChange={(event) => change({ cc: event.target.value })}
                />
              </>
            )}
            <label htmlFor="packet-subject">Subject</label>
            <input
              id="packet-subject"
              maxLength={250}
              value={packet.subject}
              onChange={(event) => change({ subject: event.target.value })}
            />
          </div>
          {type.reassurance && <div className="pp-reassurance">
            <label><input type="checkbox" checked={packet.body.includes(type.reassurance)} onChange={(event) => toggleReassurance(event.target.checked)} /> Include reassurance that pathology showed no concerning findings</label>
            <p>Select only after a clinician has confirmed the pathology results. The wording appears in the editable email below.</p>
          </div>}
          <textarea
            ref={bodyInput}
            aria-label="Email message"
            value={packet.body}
            maxLength={50000}
            onChange={(event) => change({ body: event.target.value })}
          />
          <div className="pp-attachments" aria-label="Email attachments">
            {packet.selections.map((selection, index) => (
              <span key={selection.document_id}>
                {String(index + 1).padStart(2, "0")} ·{" "}
                {documentById.get(selection.document_id)?.name ||
                  "Unavailable document"}
                {selection.pages
                  ? ` · pages ${selection.pages}`
                  : " · all pages"}
              </span>
            ))}
            {!packet.selections.length && <small>No documents selected</small>}
          </div>
          <footer className="pp-send">
            <p>
              Sending approves this selection and records the delivery in the
              patient timeline.
            </p>
            <div>
              <button type="button" className="pp-button" title="Save this draft in the app only" onClick={saveDraft}>
                Save draft
              </button>
              <button
                type="button"
                className="pp-button"
                disabled={!packet.mailboxId || gmailBlocked}
                onClick={saveGmailDraft}
              >
                Save draft in Gmail
              </button>
              <button
                type="button"
                className="pp-button pp-primary"
                disabled={
                  gmailBlocked ||
                  !packet.selections.length ||
                  !packet.to.trim() ||
                  !packet.mailboxId ||
                  !packet.subject.trim() ||
                  !packet.body.trim()
                }
                onClick={send}
              >
                Send packet ↗
              </button>
            </div>
          </footer>
        </section>
      </fieldset>
      {busy && (
        <div className="pp-working" role="status">
          {busy}
        </div>
      )}
      <div className="pp-draft-tools">
        <span
          className={`pp-status ${packet.status === "sent" ? "pp-success" : ""}`}
        >
          {packet.status === "draft"
            ? dirty
              ? "Unsaved draft"
              : "Draft saved"
            : packet.status === "sent"
              ? "Sent"
              : "Delivery needs review"}
        </span>
        {props.recentPackets.length > 0 && (
          <details className="pp-history">
            <summary>Recent packets & saved drafts</summary>
            <ul>
              {props.recentPackets.map((item) => (
                <li key={item.id}>
                  <a href={item.url}>{item.label}</a>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
      {packet.attachments.length > 0 && (
        <section className="pp-card pp-delivered">
          <h2>Saved delivery attachments</h2>
          <ul>
            {packet.attachments.map((attachment) => (
              <li key={attachment.url}>
                <a href={attachment.url} target="_blank" rel="noreferrer">
                  {attachment.name} ↗
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
